import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { UUID, Vehicle } from '@cobliteam/common-types';

import { FuscaVehiclesRequester } from '@cobliteam/api-lib';
import GrahpQLVehicleRequester from '@cobliteam/api-lib/src/dashboard/vehicle/GrahpQLVehicleRequester';
import type { NewVehicleForm } from '@cobliteam/api-lib/src/dashboard/vehicle/Types';

import { Environment } from '@cobliteam/react-environments';

import Alert from '@cobliteam/ui/components/Alert';

import { QUERY_KEY as DEVICES_QUERY_KEY } from '../useDevices';
import useUser from '../useUser';
import { QUERY_KEY as VEHICLES_QUERY_KEY } from '../useVehicle';

const env = Environment.getInstance().getEnvironment();
const fuscaVehiclesRequester = new FuscaVehiclesRequester({
  host: env.apiHost,
});
const vehicleGraphQLRequester = new GrahpQLVehicleRequester();

const QUERY_KEY = [VEHICLES_QUERY_KEY];

const useFuscaVehicle = () => {
  const queryClient = useQueryClient();
  const { useGetUserData } = useUser();
  const { data: userData } = useGetUserData();
  const userPreferences = userData?.userPreferences;
  const vehicleGroupIds = userPreferences?.vehicleGroupIds;

  const createVehicle = useMutation<NewVehicleForm, unknown, NewVehicleForm | undefined>({
    mutationFn: async (vehicle) => fuscaVehiclesRequester.createVehicle(vehicle),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY,
      });
    },
    onError: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY,
      });
      Alert.error('Erro ao criar veículo!');
    },
  });

  const createNewVehicle = useMutation<UUID, unknown, Vehicle>({
    mutationFn: async (vehicle) => vehicleGraphQLRequester.createVehicle(vehicle),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY,
      });
    },
    onError: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY,
      });
      Alert.error('Erro ao criar veículo!');
    },
  });

  const deleteVehicle = useMutation<void, unknown, UUID>({
    mutationFn: (vehicleId: UUID) => fuscaVehiclesRequester.deleteVehicle(vehicleId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY,
      });
      Alert.success('Veículo deletado com sucesso!');
    },
    onError: () => Alert.error('Erro ao deletar veículo!'),
  });

  const removeDevice = useMutation<void, unknown, UUID>({
    mutationFn: (vehicleId: UUID) => fuscaVehiclesRequester.removeDevice(vehicleId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY,
      });
      Alert.success('Dispositivo desassociado com sucesso!');
    },
    onError: () => Alert.error('Erro ao desassociar dispositivo!'),
  });

  const associateDevice = useMutation<void, unknown, { vehicleId: UUID; deviceId: string }>({
    mutationFn: (variables) => fuscaVehiclesRequester.associateDevice(variables.vehicleId, variables.deviceId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY,
      });
      Alert.success('Dispositivo associado com sucesso!');
    },
    onError: () => Alert.error('Erro ao associar dispositivo!'),
  });

  const updateVehicle = useMutation({
    mutationFn: vehicleGraphQLRequester.saveVehicle,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY,
      });
      queryClient.invalidateQueries({
        queryKey: [DEVICES_QUERY_KEY, true, true, true, false, vehicleGroupIds],
      });

      Alert.success('Veículo salvo com sucesso!');
    },
    onError: () => Alert.error('Erro ao salvar veículo!'),
  });

  return {
    createVehicle,
    createNewVehicle,
    deleteVehicle,
    removeDevice,
    associateDevice,
    updateVehicle,
  };
};

export default useFuscaVehicle;
